/**
 * Initial Blocks API — singleton-wrapped fetch for the homepage's
 * first-paint cards.
 *
 * Why this exists separately from `bootstrap-api.ts`:
 *
 *   The homepage feed is the single most important thing on cold
 *   load — cards should paint the moment the block list resolves,
 *   independent of the heavier `/v1/bootstrap` fan-out (which is
 *   gated by `collection/list`'s per-collection block-count +
 *   lastAddedAt aggregations and `pin/list`'s per-scope ACL checks).
 *   Block list is the lightest first-paint query; putting it on the
 *   same `Promise.all` as the heavier ones meant cards waited for
 *   data they didn't need.
 *
 *   Splitting blocks out lets the browser fire `/v1/bootstrap` and
 *   `/v1/block/list?limit=30` in parallel. The block call lands
 *   first under typical conditions; cards paint while bootstrap's
 *   heavier queries are still in flight, and the sidebar nav / pins
 *   fill in once bootstrap resolves.
 *
 * Why a singleton:
 *
 *   `mainStreamStore.useList`'s seed effect is the primary consumer (it
 *   ingests the top-30 envelopes into `blockStore` and seeds the homepage
 *   feed's segment bookkeeping). Any other cold-load reader that needs
 *   the same top-30 surface set can join the same promise instead of
 *   firing its own request — the singleton coalesces them into one
 *   network round-trip (same pattern as `getBootstrapPromise()`).
 *
 *   Earlier this file also seeded `blockCollection` via that collection's
 *   `queryFn`; that hookup is gone (see `block-collection.ts`'s header for
 *   why) and the homepage stream is now the only mount-time caller. The
 *   singleton stays useful for any future overlapping consumer.
 *
 * Why `waitForAuthTokenProvider`:
 *
 *   The fetch may be kicked off the moment a React subscriber mounts —
 *   potentially BEFORE `<ApiSetup>`'s `useEffect` has run
 *   `setAuthTokenProvider(getAccessToken)`. Without the wait the fetch
 *   lands with no `Authorization` header and the server returns 401.
 *   Pausing here costs nothing once auth is wired (the helper resolves
 *   synchronously after that point) and closes the cold-start race. Same
 *   rationale as bootstrap.
 *
 * Lifecycle:
 *
 *   `getInitialBlocksPromise()` — first call kicks off the fetch;
 *   subsequent calls within the same session join the same promise.
 *
 *   `resetInitialBlocks()` — clears the cached promise. Called on
 *   real sign-out from `<ApiSetup>` so the next login fetches the
 *   new user's blocks fresh.
 */
import { listBlocks } from "./query-api";
import { waitForAuthTokenProvider } from "./base-query-api";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/**
 * Page size for the homepage feed's first paint. Must stay in sync
 * with `MAX_INITIAL_LOAD` in `main-stream-store.ts` because the seed
 * uses `blocks.length >= MAX_INITIAL_LOAD` to decide if there's a
 * next page. Drift wastes bytes (server returns more than the client
 * uses) or stalls pagination (client immediately fires a redundant
 * second page).
 *
 * 30 covers the ~10 cards above the fold plus enough headroom that
 * the first scroll doesn't stall; `fetchNextPage` then runs on a
 * warm connection while the user is reading.
 */
const INITIAL_BLOCK_LIMIT = 30;
// ---------------------------------------------------------------------------
// Singleton promise
// ---------------------------------------------------------------------------
let inflight;
/**
 * Return the in-flight (or completed) initial-blocks promise for the
 * current session. First call kicks off the fetch; subsequent calls
 * join the same promise.
 *
 * On rejection the cached promise is cleared so the next caller
 * starts a fresh fetch — keeps a transient network blip from
 * permanently breaking the session.
 */
export function getInitialBlocksPromise() {
    if (inflight)
        return inflight;
    inflight = doFetchInitialBlocks().catch((e) => {
        inflight = undefined;
        throw e;
    });
    return inflight;
}
/**
 * Clear the cached initial-blocks promise. Called on real sign-out
 * from `<ApiSetup>` (gated on `hadAuthenticatedUserRef`) so the next
 * login doesn't see the previous user's data.
 */
export function resetInitialBlocks() {
    inflight = undefined;
}
// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------
async function doFetchInitialBlocks() {
    await waitForAuthTokenProvider();
    const page = await listBlocks({
        limit: INITIAL_BLOCK_LIMIT,
        orderBy: "surfaceAt",
        order: "desc",
    });
    return page.blocks;
}
